import { IonContent, IonLabel, IonPage, IonSegment, IonSegmentButton, IonSpinner, useIonToast } from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Header from "src/components/Header";
import SolicitudMantenimientoFormulario from "src/components/SolicitudMantenimientoFormulario";
import { SituacionChip } from "src/pages/SolicitudesGafcore";
import {
  CatalogoMantenimientos,
  MantenimientosAPI,
  SolicitudMantenimientoResumen,
} from "src/services/mantenimientos";
import { fmtFechaHoraTexto } from "src/services/traslados";

type Tab = "nueva" | "mis";

/**
 * Mantenimiento correctivo (/gafcore/mantenimientos): se entra por Solicitudes >
 * GafCORE > Mantenimientos. Pestaña Nueva y Mis solicitudes. Las atiende GAF en
 * GafCORE; EMSYS guarda la solicitud y trae su estado. Mismo esquema que
 * SolicitudesGafcore (traslados).
 */
const SolicitudesMantenimiento: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const location = useLocation();

  const [tab, setTab] = useState<Tab>(
    new URLSearchParams(location.search).get("tab") === "mis" ? "mis" : "nueva"
  );
  const [catalogo, setCatalogo] = useState<CatalogoMantenimientos | null>(null);
  const [mis, setMis] = useState<SolicitudMantenimientoResumen[]>([]);
  const [cargando, setCargando] = useState(true);
  // null = consultando a EMSYS; false = es operario (no pide).
  const [puede, setPuede] = useState<boolean | null>(null);
  const [mensajeSinPermiso, setMensajeSinPermiso] = useState("");

  const avisar = useCallback(
    (message: string, color = "warning") => present({ message, duration: 3500, color }),
    [present]
  );

  useEffect(() => {
    MantenimientosAPI.permiso()
      .then((r) => {
        const p = Boolean(r.data.data?.puede_solicitar);
        setPuede(p);
        setMensajeSinPermiso(r.data.data?.mensaje || "");
        if (!p) setTab("mis");
      })
      .catch(() => setPuede(true)); // si no se pudo consultar, decide EMSYS al enviar
  }, []);

  useEffect(() => {
    MantenimientosAPI.catalogo()
      .then((r) => setCatalogo(r.data.data))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudo cargar el formulario", "danger"));
  }, [avisar]);

  const cargarMis = useCallback(() => {
    setCargando(true);
    MantenimientosAPI.mis()
      .then((r) => setMis(r.data.data || []))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudieron obtener las solicitudes", "danger"))
      .finally(() => setCargando(false));
  }, [avisar]);

  useEffect(() => {
    if (tab === "mis") cargarMis();
  }, [tab, cargarMis]);

  const enviar = async (payload: Record<string, any>) => {
    try {
      const r = await MantenimientosAPI.crear(payload);
      avisar(r.data.data?.info || "Solicitud enviada a GafCORE", r.data.data?.enviada === false ? "warning" : "success");
      setTab("mis");
      return true; // el formulario se desmonta al cambiar de pestaña
    } catch (err: any) {
      avisar(err?.response?.data?.error || "No se pudo enviar la solicitud", "danger");
      return false;
    }
  };

  return (
    <IonPage>
      <Header titulo="Mantenimientos" botonIzq={{ icono: arrowBackOutline, url: "/gafcore" }} />
      <IonContent id="gafcore-mantenimientos">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)} style={{ marginBottom: 16 }}>
            <IonSegmentButton value="nueva">
              <IonLabel>Nueva</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="mis">
              <IonLabel>Mis solicitudes</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {tab === "nueva" && puede === false && (
            <p style={{ color: "#555", fontSize: 14 }}>
              {mensajeSinPermiso || "No podés cargar solicitudes de mantenimiento."}
            </p>
          )}

          {tab === "nueva" && puede !== false && !(puede && catalogo) && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          )}

          {tab === "nueva" && puede === true && catalogo && (
            <SolicitudMantenimientoFormulario catalogo={catalogo} onEnviar={enviar} onError={(m) => avisar(m)} />
          )}

          {tab === "mis" &&
            (cargando ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <IonSpinner />
              </div>
            ) : !mis.length ? (
              <p style={{ color: "#777" }}>Todavía no enviaste solicitudes de mantenimiento.</p>
            ) : (
              mis.map((s) => (
                <div
                  key={s.id}
                  onClick={() => history.push(`/gafcore/mantenimientos/${s.id}`)}
                  style={{
                    cursor: "pointer",
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    padding: "10px 12px",
                    marginBottom: 8,
                    borderLeft: `5px solid ${s.situacion_color}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <strong style={{ fontSize: 14 }}>
                      N° {s.id}
                      {s.gaf_codigo ? <span style={{ color: "#777", fontWeight: 400 }}> · {s.gaf_codigo}</span> : null}
                    </strong>
                    <SituacionChip etiqueta={s.situacion_label} color={s.situacion_color} />
                  </div>
                  <div style={{ fontSize: 13, color: "#444", marginTop: 4 }}>
                    {s.codigo_g} · {s.activo_nombre || "-"}
                  </div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>
                    {s.paro_equipo ? <span style={{ color: "#c0392b", fontWeight: 600 }}>Equipo parado · </span> : null}
                    {fmtFechaHoraTexto(s.creado_en)}
                  </div>
                </div>
              ))
            ))}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudesMantenimiento;
